import React, { Dispatch, SetStateAction } from 'react';
import { DealType } from 'constants/deals';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { NSBadge, NSCheckbox } from '../../../../bricks';
import RenderIf from '../../../../components/RenderIf/RenderIf';
import MappingTable from '../MappingTable/MappingTable';
import './MappingTables.scss';
import {
    ProFormaMappingSectionNames,
    ProFormaMappingTableHeaderNames,
    ProFormaMappingTableNames,
    createNewDevelopmentSourceRow,
    getUniqueName,
} from '../../constants';
import { IInvalidRows, IProFormaMappingRow } from '../../types';

export interface IMappingTablesProps {
    mapping: Partial<Record<string, IProFormaMappingRow[]>>;
    setMapping: Dispatch<SetStateAction<Partial<Record<string, IProFormaMappingRow[]>>>>;
    dealTypeName: string;
    isDisabled: boolean;
    useMappingForFutureDeals: boolean;
    setUseMappingForFutureDeals: Dispatch<SetStateAction<boolean>>;
    issuesCount: number;
    invalidRows: IInvalidRows;
    showMappingErrors: boolean;
}

export const MappingTables = ({
    mapping, setMapping, dealTypeName, isDisabled,
    useMappingForFutureDeals, setUseMappingForFutureDeals,
    issuesCount, invalidRows, showMappingErrors,
}: IMappingTablesProps) => {
    const handleChange = (tableName: string, rowId: string | number, col: 'sheet' | 'cell' | 'name', value: string) => {
        let finalValue = value;
        if (col === 'name') {
            const existingNames = mapping[tableName]?.map(item => item.name) || [];
            finalValue = getUniqueName(value, existingNames);
        }

        const updatedMapping = {
            ...mapping,
            [tableName]: mapping[tableName]?.map((item: IProFormaMappingRow) => (item.name === rowId
                ? { ...item, [col]: finalValue }
                : item)),
        };

        setMapping(updatedMapping);
    };

    const handleAddRow = (tableName?: string) => {
        if (!tableName) return;
        const newRow = createNewDevelopmentSourceRow(mapping[tableName] || []);
        const currentRows = mapping[tableName] || [];
        const totalRowIndex = currentRows.findIndex(row => row.name === 'Total sources');

        const updatedRows = [...currentRows];
        if (totalRowIndex !== -1) {
            updatedRows.splice(totalRowIndex, 0, newRow);
        } else {
            updatedRows.push(newRow);
        }

        const updatedMapping = {
            ...mapping,
            [tableName]: updatedRows,
        };
        setMapping(updatedMapping);
    };

    const handleDeleteRow = (tableName: string, rowId: string | number) => {
        const updatedMapping = {
            ...mapping,
            [tableName]: mapping[tableName]?.filter((item: IProFormaMappingRow) => item.id !== rowId),
        };
        setMapping(updatedMapping);
    };

    const baseColumns = (tableName: string, headerName: string, isCellEditable = false) => [
        {
            key: 'name',
            header: headerName,
            isCellEditable,
            onChange: isCellEditable
                ? (item: IProFormaMappingRow, value: string) => handleChange(tableName, item.name, 'name', value)
                : () => { },
        },
        {
            key: 'sheet',
            header: 'Sheet',
            isInput: true,
            onChange: (item: IProFormaMappingRow, value: string) => handleChange(tableName, item.name, 'sheet', value),
        },
        {
            key: 'cell',
            header: 'Cell',
            isInput: true,
            onChange: (item: IProFormaMappingRow, value: string) => handleChange(tableName, item.name, 'cell', value),
        },
    ];

    const renderTable = (
        tableName: string,
        headerName: string,
        isCellEditable: boolean = false,
    ) => {
        if (!tableName || !mapping[tableName]) return null;

        let showHeader = true;
        if (tableName === ProFormaMappingTableNames.VALUATION) showHeader = false;

        return (
            <div className="mt-1">
                <MappingTable
                    className="pb-1"
                    data={mapping[tableName]!}
                    columns={baseColumns(tableName, headerName, isCellEditable)}
                    getRowKey={item => item.id}
                    showHeader={showHeader}
                    invalidRows={invalidRows}
                    isMappingDisabled={isDisabled}
                    showMappingErrors={showMappingErrors}
                    addRowCallback={tableName => handleAddRow(tableName)}
                    tableName={tableName}
                    onDeleteRow={rowId => handleDeleteRow(tableName, rowId)}
                />
            </div>
        );
    };

    return (
        <div className={`ProFormaUploadTables NSCard--level-1 p-2 ${isDisabled ? 'ProFormaUploadTables--disabled' : ''}`}>
            <div className="d-flex flex-row justify-content-between align-items-center">
                <div className="ProFormaUploadTables--title text-white d-flex">
                    Review uploaded values

                    <RenderIf isTrue={issuesCount > 0 && !isDisabled && showMappingErrors}>
                        <NSBadge className="mx-1 bg-danger-lighten my-auto">
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="text-danger mr-1"
                            />
                            <span className="text-danger">{`${issuesCount} issue${issuesCount > 1 ? 's' : ''} to review`}</span>
                        </NSBadge>
                    </RenderIf>
                </div>
                <div className="d-flex flex-row align-items-center">
                    <NSCheckbox
                        id="usesMappingForAllFutureDeals"
                        checked={useMappingForFutureDeals}
                        labelClassName="cursor--pointer"
                        containerClassName="mr-0"
                        callback={() => setUseMappingForFutureDeals(!useMappingForFutureDeals)}
                    />
                    <div className="text-white">Use this mapping for all future development deals</div>
                </div>
            </div>
            <div className="text-muted pb-2">Map values from your uploaded file to our default metrics.</div>
            <div className="ProFormaUploadTables--separator pt-2" />

            <div className="text-muted pt-2">1 OF 3</div>
            <div className="text-white font-weight-bold pb-1">{ProFormaMappingSectionNames.RETURNS}</div>
            {renderTable(ProFormaMappingTableNames.PROPERTY_RETURNS, ProFormaMappingTableHeaderNames.PROPERTY_RETURNS)}
            {renderTable(ProFormaMappingTableNames.PARTNERSHIP_RETURNS, ProFormaMappingTableHeaderNames.PARTNERSHIP_RETURNS)}
            {renderTable(ProFormaMappingTableNames.DEVELOPMENT_RETURNS, ProFormaMappingTableHeaderNames.DEVELOPMENT_RETURNS)}

            <div className="text-muted pt-2">2 OF 3</div>
            <div className="text-white font-weight-bold pb-1">{ProFormaMappingSectionNames.OPERATIONS}</div>
            {renderTable(ProFormaMappingTableNames.OPERATIONS, ProFormaMappingTableHeaderNames.OPERATIONS)}
            {renderTable(ProFormaMappingTableNames.VALUATION, ProFormaMappingTableHeaderNames.VALUATION)}

            <RenderIf isTrue={dealTypeName === DealType.Development}>
                <div className="text-muted pt-2">3 OF 3</div>
                <div className="text-white font-weight-bold pb-1">{ProFormaMappingSectionNames.DEVELOPMENT}</div>
                {renderTable(ProFormaMappingTableNames.DEVELOPMENT_USES, ProFormaMappingTableHeaderNames.DEVELOPMENT_USES)}
                {renderTable(ProFormaMappingTableNames.DEVELOPMENT_SOURCES, ProFormaMappingTableHeaderNames.DEVELOPMENT_SOURCES, true)}
            </RenderIf>

            <RenderIf isTrue={dealTypeName === DealType.Acquisition}>
                <div className="text-muted pt-2">3 OF 3</div>
                <div className="text-white font-weight-bold pb-1">{ProFormaMappingSectionNames.ACQUISITIONS}</div>
                {renderTable(ProFormaMappingTableNames.ACQUISITIONS, ProFormaMappingTableHeaderNames.ACQUISITIONS)}
            </RenderIf>
        </div>
    );
};
